/**
 * AbrirAtencionDialog — Diálogo de apertura de atención con verificación de identidad.
 * Requiere confirmar al menos 2 de 4 identificadores del paciente.
 * La estación se recibe como prop (seleccionada previamente en la cola).
 *
 * Requirements: M4.2.3, M4.2.4
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Monitor } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import type { AbrirAtencionData, ColaAtencionItem } from "@/hooks/use-muestras";

interface AbrirAtencionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orden: ColaAtencionItem;
  /** Nombre del punto de atención (estación) ya seleccionado por el flebotomista */
  estacionNombre: string;
  onConfirm: (data: AbrirAtencionData) => void;
  isLoading?: boolean;
}

export function AbrirAtencionDialog({
  open,
  onOpenChange,
  orden,
  estacionNombre,
  onConfirm,
  isLoading,
}: AbrirAtencionDialogProps) {
  const { t } = useTranslation();

  const [verificoNombre, setVerifico_nombre] = useState(false);
  const [verificoFechaNacimiento, setVerifico_fecha_nacimiento] = useState(false);
  const [verificoIdentidad, setVerifico_identidad] = useState(false);
  const [verificoNumeroOrden, setVerifico_numero_orden] = useState(false);

  const totalConfirmados = [
    verificoNombre,
    verificoFechaNacimiento,
    verificoIdentidad,
    verificoNumeroOrden,
  ].filter(Boolean).length;

  const puedeAbrir = totalConfirmados >= 2;

  const handleConfirm = () => {
    if (!puedeAbrir) return;
    onConfirm({
      orden_id: orden.id,
      verifico_nombre: verificoNombre,
      verifico_fecha_nacimiento: verificoFechaNacimiento,
      verifico_identidad: verificoIdentidad,
      verifico_numero_orden: verificoNumeroOrden,
      estacion: estacionNombre,
    });
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      // Reset al cerrar
      setVerifico_nombre(false);
      setVerifico_fecha_nacimiento(false);
      setVerifico_identidad(false);
      setVerifico_numero_orden(false);
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("muestras.atencion.abrir_titulo")}</DialogTitle>
          <DialogDescription>
            {t("muestras.atencion.abrir_descripcion", { nombre: orden.paciente_nombre })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Info del paciente */}
          <div className="rounded-md bg-muted p-3 text-sm space-y-1">
            <p className="font-medium">{orden.paciente_nombre}</p>
            <p className="text-muted-foreground">
              {orden.numero} · {orden.paciente_edad} · {orden.paciente_sexo}
            </p>
          </div>

          {/* Estación activa (solo informativo) */}
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
            <Monitor className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium text-primary">{estacionNombre}</span>
          </div>

          {/* Verificación de identidad */}
          <div className="space-y-3">
            <p className="text-sm font-medium">{t("muestras.atencion.verificar_identidad")}</p>
            <p className="text-xs text-muted-foreground">
              {t("muestras.atencion.verificar_identidad_desc")}
            </p>

            <div className="space-y-2">
              {[
                {
                  id: "nombre",
                  label: t("muestras.atencion.verifico_nombre"),
                  checked: verificoNombre,
                  onChange: setVerifico_nombre,
                },
                {
                  id: "fecha_nacimiento",
                  label: t("muestras.atencion.verifico_fecha_nacimiento"),
                  checked: verificoFechaNacimiento,
                  onChange: setVerifico_fecha_nacimiento,
                },
                {
                  id: "identidad",
                  label: t("muestras.atencion.verifico_identidad"),
                  checked: verificoIdentidad,
                  onChange: setVerifico_identidad,
                },
                {
                  id: "numero_orden",
                  label: t("muestras.atencion.verifico_numero_orden"),
                  checked: verificoNumeroOrden,
                  onChange: setVerifico_numero_orden,
                },
              ].map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <Checkbox
                    id={item.id}
                    checked={item.checked}
                    onCheckedChange={(checked) => item.onChange(checked === true)}
                  />
                  <Label htmlFor={item.id} className="cursor-pointer">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Contador */}
            <p className="text-xs text-muted-foreground">
              {totalConfirmados}/4 {t("muestras.atencion.identificadores_confirmados")}
            </p>
          </div>

          {/* Alerta si no hay suficientes identificadores */}
          {totalConfirmados > 0 && !puedeAbrir && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{t("muestras.atencion.minimo_dos_identificadores")}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!puedeAbrir || isLoading}>
            {isLoading ? t("common.loading") : t("muestras.atencion.abrir_atencion")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
